import { useContext, useEffect, Suspense, useMemo } from "react";
import {
  BrowserRouter as Router,
  Route,
  Switch,
  Redirect,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import getCustomMuiTheme from "./theme/CustomMuiTheme";
import Home from "./components/Home";
import SignIn from "./components/SignIn";
import Layout from "./components/common/Layout";
import ResetPass from "./components/Pages/ResetPass";
import { useFirebaseAuthentication } from "./components/Firebase";
import { GlobalContext } from "./components/common/Context";
import BrowserUtils from "./components/common/Utils/BrowserUtils";
import {
  SIGNIN_PATH,
  HOME_PATH,
  RESET_PATH,
  TOKEN_SIGNIN_PATH,
  NOT_FOUND_PATH,
} from "./constants";
import { useScript } from "./components/common/Utils/";
import "./i18next";
import LoadingModal from "./components/common/LoadingModal";
import CustomToken from "./components/Pages/CustomToken";
import PageErrorBoundary from "./components/common/PageErrorBoundary";
import ErrorPage from "./components/Pages/ErrorPage";
import { CssBaseline } from "@mui/material";
import "./sentry";

const browserUtil = BrowserUtils();

const PrivateRoute = ({ children, authenticated, isLoading, ...rest }) => {
  return (
    <Route {...rest}>
      {authenticated ? (
        isLoading ? (
          <LoadingModal width={6} />
        ) : (
          children
        )
      ) : (
        <Redirect to={SIGNIN_PATH} />
      )}
    </Route>
  );
};

const PublicRoute = ({
  children,
  authenticated,
  isLoading,
  signUp,
  isSignUpPage,
  handleSignOut,
  ...rest
}) => {
  useEffect(() => {
    signUp && handleSignOut();
  }, [handleSignOut, signUp]);

  return (
    <Route {...rest}>
      {(!authenticated && !isSignUpPage) || signUp ? (
        isLoading ? (
          <LoadingModal width={6} />
        ) : (
          children
        )
      ) : authenticated ? (
        <Redirect to={HOME_PATH} />
      ) : (
        <Redirect to={SIGNIN_PATH} />
      )}
    </Route>
  );
};

const App = () => {
  useScript(
    "https://static.zdassets.com/ekr/snippet.js?key=858285f9-6708-403a-bd63-0a227dda5a64",
    "ze-snippet",
  );

  const {
    setUserBrowserName,
    setCurrentUser,
    signLoad,
    firebaseState,
    linking,
    setSignUpEmail,
    setSignUpInviteId,
    setCustomToken,
    themeMode,
    setKeyHash,
  } = useContext(GlobalContext);

  const userBrowserName = browserUtil.getUserBrowserName();
  setUserBrowserName(userBrowserName);

  const { currentUser, appFirstLoading } = useFirebaseAuthentication(
    firebaseState.firebase,
  );
  const authenticated = currentUser.authUser?.uid && !linking;
  const urlSearchParams = new URLSearchParams(window.location.search);
  const {
    e,
    i: inviteId,
    token,
    k,
  } = Object.fromEntries(urlSearchParams.entries());
  let email;
  e && (email = Buffer.from(e, "base64").toString("utf-8"));
  setSignUpEmail(email);
  setSignUpInviteId(inviteId);
  setCustomToken(token);
  setKeyHash(k);

  useEffect(() => {
    (!currentUser.authUser || !signLoad) && setCurrentUser(currentUser);
  }, [signLoad, currentUser, setCurrentUser]);

  const customTheme = useMemo(() => getCustomMuiTheme(themeMode), [themeMode]);

  return (
    <Router>
      <ThemeProvider theme={customTheme}>
        <CssBaseline />
        <Suspense fallback={<LoadingModal width={6} />}>
          <Layout isLoggedIn={authenticated}>
            <Switch>
              <PublicRoute
                exact
                path={SIGNIN_PATH}
                authenticated={authenticated}
                isLoading={appFirstLoading}>
                <PageErrorBoundary>
                  <SignIn />
                </PageErrorBoundary>
              </PublicRoute>
              <PrivateRoute
                path={HOME_PATH}
                authenticated={authenticated}
                isLoading={appFirstLoading}>
                <Home />
              </PrivateRoute>
              <PublicRoute
                path={RESET_PATH}
                authenticated={authenticated}
                isLoading={appFirstLoading}
                signUp={email}
                isSignUpPage
                handleSignOut={firebaseState.doSignOut}>
                <PageErrorBoundary>
                  <ResetPass />
                </PageErrorBoundary>
              </PublicRoute>
              <PublicRoute
                path={TOKEN_SIGNIN_PATH}
                authenticated={authenticated}
                isLoading={appFirstLoading}>
                <PageErrorBoundary>
                  <CustomToken />
                </PageErrorBoundary>
              </PublicRoute>
              <Route path={NOT_FOUND_PATH}>
                <ErrorPage pageNotFound />
              </Route>
              <Redirect to={NOT_FOUND_PATH} />
            </Switch>
          </Layout>
        </Suspense>
      </ThemeProvider>
    </Router>
  );
};

export default App;
